import React, { useState } from 'react';
import MobileTimeWheelPicker from './MobileTimeWheelPicker';

/**
 * MobileSlotAdder - Component for adding time slots on mobile
 *
 * Props:
 * - selectedDate: Date - Currently selected date
 * - onAddSlot: (startTime, endTime) => void - Callback when slot is added
 * - existingSlots: Array - Existing slots for validation
 */
const MobileSlotAdder = ({ selectedDate, onAddSlot, existingSlots = [] }) => {
  // Calculate default start time (current time rounded up to next 15-minute interval)
  const getDefaultStartTime = () => {
    const now = new Date();
    const currentHours = now.getHours();
    const currentMinutes = now.getMinutes();

    // Round up to next 15-minute interval
    const roundedMinutes = Math.ceil(currentMinutes / 15) * 15;

    if (roundedMinutes === 60) {
      // If rounded to 60, move to next hour
      return {
        hours: (currentHours + 1) % 24,
        minutes: 0
      };
    }

    return {
      hours: currentHours,
      minutes: roundedMinutes
    };
  };

  const [startTime, setStartTime] = useState(getDefaultStartTime());
  const [duration, setDuration] = useState({ hours: 1, minutes: 0 });

  // Format selected date
  const formatSelectedDate = (date) => {
    const dayName = date.toLocaleDateString('en-US', { weekday: 'long' });
    const month = date.toLocaleDateString('en-US', { month: 'short' });
    const day = date.getDate();
    return `${dayName}, ${month} ${day}`;
  };

  // Calculate end time based on start time and duration
  const calculateEndTime = () => {
    const totalMinutes = (startTime.hours * 60 + startTime.minutes) +
                        (duration.hours * 60 + duration.minutes);

    const endHours = Math.floor(totalMinutes / 60) % 24;
    const endMinutes = totalMinutes % 60;

    return { hours: endHours, minutes: endMinutes };
  };

  // Format time for display
  const formatTime = (time) => {
    const h = String(time.hours).padStart(2, '0');
    const m = String(time.minutes).padStart(2, '0');
    return `${h}:${m}`;
  };

  // Validate slot doesn't overlap with existing slots
  const validateSlot = (start, end) => {
    const startMinutes = start.hours * 60 + start.minutes;
    const endMinutes = end.hours * 60 + end.minutes;

    // Check if duration is valid (end after start)
    if (endMinutes <= startMinutes) {
      return { valid: false, message: 'End time must be after start time' };
    }

    // Check for overlaps with existing slots
    for (const slot of existingSlots) {
      let slotStartMinutes, slotEndMinutes;

      // Handle both date formats
      if (slot.start && slot.end) {
        const slotStart = new Date(slot.start);
        const slotEnd = new Date(slot.end);
        slotStartMinutes = slotStart.getHours() * 60 + slotStart.getMinutes();
        slotEndMinutes = slotEnd.getHours() * 60 + slotEnd.getMinutes();
      } else if (slot.startTime && slot.endTime) {
        const [startHour, startMin] = slot.startTime.split(':').map(Number);
        const [endHour, endMin] = slot.endTime.split(':').map(Number);
        slotStartMinutes = startHour * 60 + startMin;
        slotEndMinutes = endHour * 60 + endMin;
      } else {
        continue; // Skip invalid slots
      }

      // Check overlap: newStart < existingEnd && newEnd > existingStart
      if (startMinutes < slotEndMinutes && endMinutes > slotStartMinutes) {
        const hours = Math.floor(slotStartMinutes / 60);
        const minutes = slotStartMinutes % 60;
        const endHours = Math.floor(slotEndMinutes / 60);
        const endMinutes = slotEndMinutes % 60;

        return {
          valid: false,
          message: `This time overlaps with existing slot ${formatTime({ hours, minutes })} - ${formatTime({ hours: endHours, minutes: endMinutes })}`
        };
      }
    }

    return { valid: true };
  };

  // Handle add slot
  const handleAddSlot = () => {
    const endTime = calculateEndTime();
    const validation = validateSlot(startTime, endTime);

    if (!validation.valid) {
      // Show error toast (will be handled by parent component)
      onAddSlot(null, null, validation.message);
      return;
    }

    // Create start and end Date objects
    const startDate = new Date(selectedDate);
    startDate.setHours(startTime.hours, startTime.minutes, 0, 0);

    const endDate = new Date(selectedDate);
    endDate.setHours(endTime.hours, endTime.minutes, 0, 0);

    // If end time is past midnight, add a day
    if (endTime.hours < startTime.hours ||
        (endTime.hours === startTime.hours && endTime.minutes < startTime.minutes)) {
      endDate.setDate(endDate.getDate() + 1);
    }

    onAddSlot(startDate, endDate);
  };

  const endTime = calculateEndTime();

  return (
    <div className="w-full bg-white dark:bg-gray-800 rounded-lg shadow-md p-4">
      {/* Header */}
      <div className="mb-4">
        <h3 className="text-base font-semibold text-gray-900 dark:text-white">
          Add Time Slot
        </h3>
        <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
          {formatSelectedDate(selectedDate)}
        </p>
      </div>

      {/* Time pickers */}
      <div className="space-y-4">
        <MobileTimeWheelPicker
          label="Start Time"
          type="time"
          value={startTime}
          onChange={setStartTime}
        />

        <MobileTimeWheelPicker
          label="Duration"
          type="duration"
          value={duration}
          onChange={setDuration}
        />
      </div>

      {/* Preview */}
      <div className="mt-4 p-3 bg-gray-50 dark:bg-gray-700 rounded-lg">
        <div className="text-sm text-gray-600 dark:text-gray-400">
          Time slot will be:
        </div>
        <div className="text-lg font-semibold text-gray-900 dark:text-white mt-1">
          {formatTime(startTime)} - {formatTime(endTime)}
        </div>
      </div>

      {/* Add button */}
      <button
        onClick={handleAddSlot}
        className="
          w-full mt-4 h-12
          bg-blue-500 hover:bg-blue-600
          active:scale-98
          text-white font-semibold rounded-lg
          transition-all duration-150
          touch-manipulation
          shadow-md hover:shadow-lg
        "
      >
        ✓ Add Time Slot
      </button>
    </div>
  );
};

export default MobileSlotAdder;
